import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCheckSquare, FiUsers, FiBriefcase, FiGift, FiClock, FiAlertCircle, FiFolder } from 'react-icons/fi';
import api from '../services/api';

const Spinner = () => (
  <div className="flex items-center justify-center py-10">
    <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const StatSkeleton = () => (
  <div className="bg-white rounded-lg shadow-card p-6 animate-pulse">
    <div className="flex items-center">
      <div className="w-12 h-12 rounded-full bg-gray-200"></div>
      <div className="ml-4 flex-1">
        <div className="h-3 bg-gray-200 rounded w-24 mb-2"></div>
        <div className="h-7 bg-gray-200 rounded w-12 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-40"></div>
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingBirthdays, setLoadingBirthdays] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
    fetchProjectTasks();
  }, []);

  const fetchProjectTasks = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const projectsRes = await api.post('/projects/list', { limit: 100 });
      const projects = projectsRes.data.projects || [];
      const results = await Promise.all(
        projects.map(async (project) => {
          try {
            const res = await api.post(`/projects/${project.id}/tasks`);
            const allTasks = Array.isArray(res.data) ? res.data : (res.data.tasks || []);
            const myTasks = allTasks.filter(t =>
              t.status !== 'completed' &&
              t.Users && t.Users.some(u => u.id === currentUser.id)
            );
            return myTasks.length > 0 ? { project, tasks: myTasks } : null;
          } catch { return null; }
        })
      );
      setProjectTasks(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching project tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const myStatsResponse = await api.get('/dashboard/employee');
      const myTasksCount = myStatsResponse.data.totalTasks || 0;
      const myTasksByStatus = myStatsResponse.data.tasksByStatus || [];
      const completedTasks = myTasksByStatus.find(t => t._id === 'completed')?.count || 0;
      const overdueTasks = myStatsResponse.data.overdueTasks || 0;

      const usersResponse = await api.get('/users', { params: { limit: 1000 } });
      const users = usersResponse.data.users || usersResponse.data;
      const totalUsersCount = usersResponse.data.pagination?.totalItems ?? users.length;

      const clientsResponse = await api.get('/clients');
      const projectsResponse = await api.get('/projects');

      setStats({
        totalTasks: myTasksCount,
        completedTasks,
        pendingTasks: myTasksCount - completedTasks,
        overdueTasks,
        totalUsers: totalUsersCount,
        totalClients: Array.isArray(clientsResponse.data) ? clientsResponse.data.length : (clientsResponse.data.clients?.length ?? 0),
        totalProjects: Array.isArray(projectsResponse.data) ? projectsResponse.data.length : (projectsResponse.data.projects || []).length
      });
      setLoadingStats(false);

      // Birthdays from users already fetched
      const now = new Date();
      const threeMonthsFromNow = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());
      const birthdaysNext3Months = users.filter(user => {
        if (!user.date_of_birth) return false;
        const birthDate = new Date(user.date_of_birth);
        const thisYear = now.getFullYear();
        let nextBirthday = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
        if (nextBirthday < now) nextBirthday = new Date(thisYear + 1, birthDate.getMonth(), birthDate.getDate());
        return nextBirthday <= threeMonthsFromNow;
      }).map(user => {
        const birthDate = new Date(user.date_of_birth);
        const thisYear = now.getFullYear();
        let nextBirthday = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
        if (nextBirthday < now) nextBirthday = new Date(thisYear + 1, birthDate.getMonth(), birthDate.getDate());
        return { ...user, nextBirthday, daysUntil: Math.ceil((nextBirthday - now) / (1000 * 60 * 60 * 24)) };
      }).sort((a, b) => a.daysUntil - b.daysUntil);
      setUpcomingBirthdays(birthdaysNext3Months);
      setLoadingBirthdays(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setLoadingStats(false);
      setLoadingBirthdays(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-secondary-900 mb-6">Admin Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {loadingStats ? (
          <><StatSkeleton /><StatSkeleton /><StatSkeleton /></>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary-100 text-primary-600"><FiCheckSquare size={24} /></div>
                <div className="ml-4">
                  <h2 className="text-sm font-medium text-secondary-500">My Tasks</h2>
                  <p className="text-2xl font-semibold text-secondary-900">{stats.totalTasks}</p>
                  <div className="mt-1 text-xs">
                    <span className="text-success">{stats.completedTasks} completed</span>
                    {' • '}
                    <span className="text-secondary-500">{stats.pendingTasks} pending</span>
                    {' • '}
                    <span className="text-danger">{stats.overdueTasks} overdue</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary-100 text-primary-600"><FiUsers size={24} /></div>
                <div className="ml-4">
                  <h2 className="text-sm font-medium text-secondary-500">Team Members</h2>
                  <p className="text-2xl font-semibold text-secondary-900">{stats.totalUsers}</p>
                  <div className="mt-1 text-xs text-secondary-500">
                    <Link to="/team" className="text-primary-600 hover:underline">Manage team</Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary-100 text-primary-600"><FiBriefcase size={24} /></div>
                <div className="ml-4">
                  <h2 className="text-sm font-medium text-secondary-500">Clients</h2>
                  <p className="text-2xl font-semibold text-secondary-900">{stats.totalClients}</p>
                  <div className="mt-1 text-xs text-secondary-500">
                    <Link to="/clients" className="text-primary-600 hover:underline">Manage clients</Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Upcoming Tasks + Admin Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        {/* Upcoming Tasks */}
        <div className="bg-white rounded-lg shadow-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600"><FiClock size={20} /></div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h2>
              {!loadingTasks && <p className="text-xs text-gray-400">{projectTasks.flatMap(p => p.tasks).length} active tasks assigned to you</p>}
            </div>
          </div>
          {loadingTasks ? <Spinner /> : projectTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <FiCheckSquare size={36} className="mb-2 opacity-30" />
              <p className="text-sm">No upcoming tasks assigned to you.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {projectTasks.flatMap(({ project, tasks }) =>
                tasks.map(task => {
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                  const dueDateLabel = task.due_date
                    ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'No due date';
                  const statusColor =
                    task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    task.status === 'testing' ? 'bg-purple-100 text-purple-700' :
                    task.status === 'client_review' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-600';
                  const priorityColor =
                    task.priority === 'high' ? 'bg-red-500 text-white' :
                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700';
                  const rowBorder = task.priority === 'high' ? 'border-l-4 border-red-400' : 'border-l-4 border-transparent';
                  return (
                    <div key={task.id} className={`flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition ${rowBorder}`}>
                      <div className="flex-1 min-w-0 mr-3">
                        <Link to={`/admin/tasks/${task.id}`} className="text-sm font-medium text-gray-900 hover:text-indigo-600 truncate block">
                          {task.title}
                        </Link>
                        <Link to={`/admin/projects/${project.id}/tasks`} className="text-xs text-gray-400 hover:text-indigo-500 flex items-center gap-1">
                          <FiFolder size={10} /> {project.name}
                        </Link>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${statusColor}`}>
                          {(task.status || '').replace(/_/g, ' ')}
                        </span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${priorityColor} ${task.priority === 'high' ? 'animate-pulse' : ''}`}>
                          {task.priority}
                        </span>
                        <span className={`flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${isOverdue ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'}`}>
                          {isOverdue && <FiAlertCircle size={10} />}
                          {dueDateLabel}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Admin Controls */}
        <div className="bg-white rounded-lg shadow-card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4">Admin Controls</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/team" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">User Management</h3>
              <p className="text-sm text-secondary-500">Add, edit, or remove team members</p>
            </Link>
            <Link to="/clients" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">Client Management</h3>
              <p className="text-sm text-secondary-500">Manage client information</p>
            </Link>
            <Link to="/settings" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">System Settings</h3>
              <p className="text-sm text-secondary-500">Configure application settings</p>
            </Link>
            <Link to="/admin/monthly-report" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">Monthly Reports</h3>
              <p className="text-sm text-secondary-500">View detailed employee time tracking analytics</p>
            </Link>
          </div>
        </div>
      </div>

      {/* Upcoming Birthdays */}
      <div className="bg-white rounded-lg shadow-card p-6 mb-8">
        <div className="flex items-center mb-4">
          <div className="p-2 rounded-full bg-pink-100 text-pink-600 mr-3"><FiGift size={20} /></div>
          <h2 className="text-lg font-medium text-secondary-900">Upcoming Birthdays</h2>
        </div>
        {loadingBirthdays ? <Spinner /> : upcomingBirthdays.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No upcoming birthdays in the next 3 months.</p>
        ) : (
          <div className="space-y-3">
            {upcomingBirthdays.slice(0, 5).map(user => (
              <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-full bg-pink-200 flex items-center justify-center mr-3">
                    <span className="text-pink-600 font-medium text-sm">{user.name.split(' ').map(n => n[0]).join('')}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.nextBirthday.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
                <p className="text-sm font-medium text-pink-600">
                  {user.daysUntil === 0 ? 'Today!' : user.daysUntil === 1 ? 'Tomorrow' : `${user.daysUntil} days`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
